/**
 * Gate State Machine (ADR-0004)
 *
 * Defines the finite-state gating model for the auth/onboarding/bootstrap refactor.
 * This is the target architecture - GateController will derive GateState from
 * the session-bootstrap response (GateSnapshot).
 *
 * Key invariants:
 * - Exactly one GateState applies at any time (mutually exclusive)
 * - GateState is derived from server truth (GateSnapshot), never invented client-side
 * - ERROR_BOOTSTRAP shows recovery UI, never defaults to ACTIVE
 */

import type { BootstrapResponse, AccountStatus, OnboardingStatus, GatingReason } from './auth-state';

// -----------------------------------------------------------------------------
// GATE STATE (Finite State Machine)
// -----------------------------------------------------------------------------

/**
 * The 6 explicit gate states - the app MUST render exactly one screen per state.
 * These replace the competing AUTHENTICATED/GATED states with clearer semantics.
 */
export type GateState =
  | 'UNAUTHENTICATED'      // No Supabase session
  | 'BOOTING'              // Session exists; fetching bootstrap (transient)
  | 'ERROR_BOOTSTRAP'      // Bootstrap failed; show recovery UI
  | 'ONBOARDING_REQUIRED'  // Must complete onboarding flow
  | 'PENDING_APPROVAL'     // Awaiting admin approval
  | 'DENIED'               // REJECTED or SUSPENDED (terminal)
  | 'ACTIVE';              // Full access to clinical features

// -----------------------------------------------------------------------------
// BOOTSTRAP ERROR CODES
// -----------------------------------------------------------------------------

/**
 * Explicit error codes for bootstrap failures (PHI-safe).
 * Used to render appropriate recovery UI.
 */
export type BootstrapErrorCode =
  | 'BOOTSTRAP_401_UNAUTHORIZED'  // Missing/invalid/expired token
  | 'BOOTSTRAP_403_FORBIDDEN'     // User gated/denied by server
  | 'BOOTSTRAP_CORS_BLOCKED'      // Browser blocked request
  | 'BOOTSTRAP_NETWORK'           // Offline/DNS failure
  | 'BOOTSTRAP_TIMEOUT'           // Request exceeded timeout
  | 'BOOTSTRAP_5XX';              // Server error

// -----------------------------------------------------------------------------
// GATE SNAPSHOT (Server Response Model)
// -----------------------------------------------------------------------------

/**
 * Extended snapshot from session-bootstrap edge function.
 * This is the authoritative source for all gating decisions.
 *
 * Note: This extends BootstrapResponse with additional fields for
 * cache management and policy versioning.
 */
export interface GateSnapshot extends BootstrapResponse {
  /** Unix timestamp when snapshot was fetched */
  fetched_at: number;
  /** Policy version for cache invalidation (optional, future use) */
  policy_version?: string;
}

// -----------------------------------------------------------------------------
// GATE CONTEXT (for GateController)
// -----------------------------------------------------------------------------

/**
 * The context provided by GateController.
 * This will be the single source of truth for gating decisions.
 */
export interface GateContext {
  // State machine
  gateState: GateState;
  gateSnapshot: GateSnapshot | null;

  // Error info (when gateState === 'ERROR_BOOTSTRAP')
  errorCode: BootstrapErrorCode | null;
  errorMessage: string | null;

  // Supabase session (for token access)
  session: import('@supabase/supabase-js').Session | null;
  user: import('@supabase/supabase-js').User | null;

  // Derived convenience (from snapshot)
  isAdmin: boolean;
  canAccessClinical: boolean;
  role: 'radiologist' | 'admin' | null;

  // Actions
  retryBootstrap: () => Promise<void>;
  signOut: () => Promise<void>;
}

// -----------------------------------------------------------------------------
// STATE DERIVATION
// -----------------------------------------------------------------------------

/**
 * Derive GateState from session and bootstrap snapshot.
 *
 * Rules (in order of precedence):
 * 1. No session → UNAUTHENTICATED
 * 2. Session but no snapshot → BOOTING
 * 3. Bootstrap error → ERROR_BOOTSTRAP
 * 4. Account REJECTED or SUSPENDED → DENIED
 * 5. Gating reason = ONBOARDING_REQUIRED → ONBOARDING_REQUIRED
 * 6. Account pending_approval → PENDING_APPROVAL
 * 7. can_access_clinical = true → ACTIVE
 * 8. Default (safe) → PENDING_APPROVAL
 */
export function deriveGateState(
  session: import('@supabase/supabase-js').Session | null,
  snapshot: GateSnapshot | null,
  bootstrapError: BootstrapErrorCode | null
): GateState {
  // 1. No session
  if (!session) {
    return 'UNAUTHENTICATED';
  }

  // 2. Session but no snapshot (still fetching)
  if (!snapshot && !bootstrapError) {
    return 'BOOTING';
  }

  // 3. Bootstrap error
  if (bootstrapError) {
    return 'ERROR_BOOTSTRAP';
  }

  // At this point, snapshot is guaranteed non-null
  const snap = snapshot!;

  // 4. Terminal denied states
  if (snap.account_status === 'rejected' || snap.account_status === 'suspended') {
    return 'DENIED';
  }

  // 5. Onboarding required
  if (snap.gating_reason === 'ONBOARDING_REQUIRED') {
    return 'ONBOARDING_REQUIRED';
  }

  // 6. Pending approval
  if (
    snap.account_status === 'pending_approval' ||
    snap.account_status === 'pending' ||
    snap.gating_reason === 'PENDING_APPROVAL'
  ) {
    return 'PENDING_APPROVAL';
  }

  // 7. Active access
  if (snap.can_access_clinical) {
    return 'ACTIVE';
  }

  // 8. Safe default (should not reach here with well-formed snapshot)
  console.warn('[GateState] Unexpected state, defaulting to PENDING_APPROVAL', {
    account_status: snap.account_status,
    gating_reason: snap.gating_reason,
    can_access_clinical: snap.can_access_clinical,
  });
  return 'PENDING_APPROVAL';
}

/**
 * Classify HTTP/network errors into BootstrapErrorCode.
 */
export function classifyBootstrapError(
  status: number | null,
  errorType: 'network' | 'timeout' | 'cors' | 'http' | 'unknown'
): BootstrapErrorCode {
  // Network-level errors
  if (errorType === 'timeout') {
    return 'BOOTSTRAP_TIMEOUT';
  }
  if (errorType === 'network') {
    return 'BOOTSTRAP_NETWORK';
  }
  if (errorType === 'cors') {
    return 'BOOTSTRAP_CORS_BLOCKED';
  }

  // HTTP status errors
  if (status === 401) {
    return 'BOOTSTRAP_401_UNAUTHORIZED';
  }
  if (status === 403) {
    return 'BOOTSTRAP_403_FORBIDDEN';
  }
  if (status && status >= 500) {
    return 'BOOTSTRAP_5XX';
  }

  // Default
  return 'BOOTSTRAP_NETWORK';
}

// -----------------------------------------------------------------------------
// TELEMETRY TYPES
// -----------------------------------------------------------------------------

/**
 * Gate transition event for telemetry (PHI-safe).
 */
export interface GateTransitionEvent {
  event_type: 'GATE_TRANSITION';
  from_state: GateState | null;
  to_state: GateState;
  trigger: 'boot' | 'bootstrap_success' | 'bootstrap_error' | 'sign_out' | 'retry' | 'token_refresh';
  error_code?: BootstrapErrorCode;
  duration_ms?: number;
  route?: string;
}

// -----------------------------------------------------------------------------
// CONSTANTS
// -----------------------------------------------------------------------------

/** Bootstrap fetch timeout (hard limit) */
export const BOOTSTRAP_TIMEOUT_MS = 3000;

/** Cache staleness threshold */
export const BOOTSTRAP_CACHE_STALE_MS = 5 * 60 * 1000; // 5 minutes

/** Session storage key for cached snapshot (UX improvement only) */
export const GATE_SNAPSHOT_CACHE_KEY = 'gate_snapshot_cache';

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

/**
 * Check if a GateState allows clinical access.
 */
export function canAccessClinicalForState(state: GateState): boolean {
  return state === 'ACTIVE';
}

/**
 * Check if a GateState requires showing a blocking screen.
 */
export function isBlockingState(state: GateState): boolean {
  return state !== 'ACTIVE' && state !== 'BOOTING';
}

/**
 * Get human-readable label for GateState (for UI).
 */
export function getGateStateLabel(state: GateState): string {
  switch (state) {
    case 'UNAUTHENTICATED':
      return 'Not signed in';
    case 'BOOTING':
      return 'Loading...';
    case 'ERROR_BOOTSTRAP':
      return 'Connection error';
    case 'ONBOARDING_REQUIRED':
      return 'Onboarding required';
    case 'PENDING_APPROVAL':
      return 'Pending approval';
    case 'DENIED':
      return 'Access denied';
    case 'ACTIVE':
      return 'Active';
  }
}

/**
 * Get human-readable message for BootstrapErrorCode (for UI).
 */
export function getBootstrapErrorMessage(code: BootstrapErrorCode): string {
  switch (code) {
    case 'BOOTSTRAP_401_UNAUTHORIZED':
      return 'Your session has expired. Please sign in again.';
    case 'BOOTSTRAP_403_FORBIDDEN':
      return 'You do not have access to this application.';
    case 'BOOTSTRAP_CORS_BLOCKED':
      return 'Unable to connect to the server. Please check your network settings.';
    case 'BOOTSTRAP_NETWORK':
      return 'Unable to connect. Please check your internet connection.';
    case 'BOOTSTRAP_TIMEOUT':
      return 'The server is taking too long to respond. Please try again.';
    case 'BOOTSTRAP_5XX':
      return 'The server encountered an error. Please try again later.';
  }
}
